import React, {useMemo, useState} from 'react';
import {Icons} from '../../../../assets';
import {Cred} from '../../../../models';

interface PublicCredCardProps {
  cred: Cred;
}

export const PublicCredCard: React.FC<PublicCredCardProps> = ({cred}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const primaryResource = cred.resources?.[0];
  const hasMedia = Boolean(primaryResource?.url);

  const formattedDate = useMemo(() => {
    if (!cred.createdAt) {
      return '';
    }
    return cred.createdAt.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
  }, [cred.createdAt]);

  return (
    <article className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
      {hasMedia && (
        <div className="relative aspect-[4/3] w-full overflow-hidden bg-gray-100">
          {primaryResource?.type === 'video' ? (
            <video playsInline controls poster={primaryResource.url} src={primaryResource.url} className="h-full w-full object-cover" />
          ) : (
            <img src={primaryResource?.url} alt="" className="h-full w-full object-cover" />
          )}
        </div>
      )}

      <div className="flex flex-col gap-3 px-3.5 py-2.5">
        <div className="space-y-2.5">
          <div className="flex items-center gap-2.5 text-xs text-gray-500">
            <span className="uppercase tracking-wider font-medium">Cred</span>
            {formattedDate && (
              <>
                <span className="h-1 w-1 rounded-full bg-gray-400" />
                <span className="text-gray-500">{formattedDate}</span>
              </>
            )}
          </div>
          <p className={`text-base font-semibold leading-snug text-gray-900 ${isExpanded ? '' : 'line-clamp-2'}`}>{cred.description}</p>
          {!isExpanded && cred.description?.length > 120 && (
            <button onClick={() => setIsExpanded(true)} className="text-sm font-semibold text-blue-600 hover:text-blue-700">
              read more
            </button>
          )}
        </div>

        {(cred.skills && cred.skills.length > 0) || (cred.organizations && cred.organizations.length > 0) ? (
          <div className="flex flex-wrap gap-2">
            {cred.skills && cred.skills.length > 0 && (
              <>
                {cred.skills.slice(0, 3).map(skill => (
                  <span key={skill.id} className="rounded-full bg-purple-50 px-3 py-1 text-xs font-medium text-purple-700">
                    {skill.skill?.name}
                  </span>
                ))}
                {cred.skills.length > 3 && (
                  <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
                    +{cred.skills.length - 3}
                  </span>
                )}
              </>
            )}
            {cred.organizations && cred.organizations.length > 0 && (
              <>
                {cred.organizations.map(org => (
                  <span
                    key={org.id}
                    className="rounded-lg border border-gray-200 bg-white/50 px-2.5 py-1 text-xs font-medium text-gray-700">
                    {org.organization?.name}
                  </span>
                ))}
              </>
            )}
          </div>
        ) : null}

        <div className="flex items-center justify-between rounded-xl bg-gray-50/80 px-3.5 py-2.5 text-xs text-gray-700">
          <div className="flex items-center gap-1.5">
            <img src={Icons.HI_FI_ICON} alt="" className="h-4 w-4" />
            <span className="font-semibold">{cred.hifiCount ?? 0}</span>
            <span className="text-gray-500">Hi-fis</span>
          </div>
          <div className="flex items-center gap-1.5">
            <img src={Icons.RECCO_ICON_V2} alt="" className="h-4 w-4" />
            <span className="font-semibold">{cred.reccoCount ?? 0}</span>
            <span className="text-gray-500">Reccos</span>
          </div>
          <div className="flex items-center gap-1.5">
            <img src={Icons.RECCO_SHARES} alt="" className="h-4 w-4" />
            <span className="font-semibold">{cred.shareCount ?? 0}</span>
            <span className="text-gray-500">Shares</span>
          </div>
        </div>
      </div>
    </article>
  );
};
